// The request form itself — shared by both the guest path (after OTP
// verification + details) and the account-holder path
// (pages/RequestFormPage.jsx). neededBy/purpose/buyerNote + a dynamic list of lines
// (rawDescription/rawUnit/qty), add/remove, at least 1 line required to
// submit (client-side mirror of the server's EMPTY_REQUEST). The submit
// button sits in a sticky footer so it stays reachable in the lower half of
// the screen regardless of how many lines are open above it (portal/
// CLAUDE.md: "primary actions in the lower half of the screen").
//
// Draft persistence: `onDraftChange(values)` fires on every change (RHF's
// own watch, not debounced — this form is small enough that this is cheap)
// so the parent can mirror it to localStorage via usePersistedState. This
// component itself holds no opinion on WHERE the draft is stored — see
// pages/GuestPage.jsx and pages/RequestFormPage.jsx for the two callers.
//
// On submit failure, nothing is cleared or reset — react-hook-form's own
// state already does this by default (only a successful, explicit
// `reset()` call clears fields, never called here on error) — the user's
// typed lines are still exactly as they left them, matching portal/
// CLAUDE.md: "never clear the form on a failed submit."

import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Plus, Search, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  RAW_DESCRIPTION_MAX,
  RAW_UNIT_MAX,
  validateQty,
  validateRawDescription,
  validateRawUnit,
} from '../schemas/requestLineSchema.js';
import ErrorState from './ErrorState.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import UploadDropZone from './UploadDropZone.jsx';
import { consumePendingUploadFocus } from '../utils/pendingUploadFocus.js';
import { usePortalProducts } from '../hooks/usePublicCatalog.js';
import { buildLineFromProduct, conditionLabelId } from '../utils/productDisplay.js';
import styles from './RequestLinesForm.module.css';

const EMPTY_LINE = { rawDescription: '', rawUnit: '', qty: '' };

// Unit dropdown options (unit-dropdown task) — `value` is the exact
// canonical token submitted as `rawUnit`; `labelId` is the translated
// display label (shared/i18n/portal.js). "Other" (UnitField's own sentinel
// below, not listed here) reveals a free-text input instead.
const UNIT_OPTIONS = [
  { value: 'pcs', labelId: 'portal.requestForm.unitPcs' },
  { value: 'carton', labelId: 'portal.requestForm.unitCarton' },
  { value: 'box', labelId: 'portal.requestForm.unitBox' },
  { value: 'set', labelId: 'portal.requestForm.unitSet' },
  { value: 'pack', labelId: 'portal.requestForm.unitPack' },
  { value: 'roll', labelId: 'portal.requestForm.unitRoll' },
  { value: 'metre', labelId: 'portal.requestForm.unitMetre' },
  { value: 'kg', labelId: 'portal.requestForm.unitKg' },
  { value: 'litre', labelId: 'portal.requestForm.unitLitre' },
  { value: 'ream', labelId: 'portal.requestForm.unitReam' },
];
const UNIT_VALUES = new Set(UNIT_OPTIONS.map((opt) => opt.value));
const OTHER_UNIT = '__other__';

// The small "name + spec beneath + quiet catalog marker" header shown above
// a catalog-sourced line's (still fully editable) description field —
// catalog-picker task. Pulled out as its own component, not inlined in the
// row map below, because matchedProductId/productName/productSpec are
// never registered inputs (nothing visible binds to them) — reading them
// needs useWatch, and useWatch is a hook, so it needs its own component to
// call per row legally. Written via setValue (RequestLinesForm's own
// handlePickFromCatalog), the exact same setValue-to-write/useWatch-to-read
// shape admin/src/components/PricingLineRow.jsx already uses for its own
// unregistered-but-tracked `productId` field — proven to work in this
// codebase already, not a guess for this one.
function CatalogLineInfo({ control, index }) {
  const { t } = useLanguage();
  const matchedProductId = useWatch({ control, name: `lines.${index}.matchedProductId` });
  const productName = useWatch({ control, name: `lines.${index}.productName` });
  const productSpec = useWatch({ control, name: `lines.${index}.productSpec` });
  if (!matchedProductId) return null;
  return (
    <div className={styles.catalogLineInfo}>
      <p className={styles.catalogLineName}>{productName}</p>
      {productSpec && <p className={styles.catalogLineSpec}>{productSpec}</p>}
      <span className={styles.catalogLineMarker}>{t('portal.requestForm.fromCatalog')}</span>
    </div>
  );
}

// Unit field (unit-dropdown task): a managed <select> of common units plus
// an "Other" option that reveals a free-text input alongside it — the
// select itself never disappears, so switching back to a listed unit is
// always one click away (unlike a true replace-in-place reveal, which would
// strand a buyer who picked "Other" by mistake with no way back). A custom
// value already in the field — restored from a draft, or typed before this
// task shipped — keeps showing in the "Other" input rather than silently
// blanking (same stance the catalog brand dropdown, entry 129, took for an
// unlisted legacy brand). Pulled out as its own component for the same
// reason CatalogLineInfo above is: useWatch is a hook, so it needs its own
// component to call once per row legally.
function UnitField({ control, register, setValue, index }) {
  const { t } = useLanguage();
  const rawUnit = useWatch({ control, name: `lines.${index}.rawUnit` });
  const [otherSelected, setOtherSelected] = useState(
    () => Boolean(rawUnit) && !UNIT_VALUES.has(rawUnit)
  );

  const selectValue = otherSelected ? OTHER_UNIT : UNIT_VALUES.has(rawUnit) ? rawUnit : '';

  const handleSelectChange = (e) => {
    const value = e.target.value;
    if (value === OTHER_UNIT) {
      setOtherSelected(true);
      setValue(`lines.${index}.rawUnit`, '');
      return;
    }
    setOtherSelected(false);
    setValue(`lines.${index}.rawUnit`, value);
  };

  return (
    <>
      <select
        className={styles.input}
        value={selectValue}
        onChange={handleSelectChange}
        aria-label={t('portal.requestForm.lineUnit')}
      >
        <option value="">{t('portal.requestForm.unitUnspecified')}</option>
        {UNIT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {t(opt.labelId)}
          </option>
        ))}
        <option value={OTHER_UNIT}>{t('portal.requestForm.unitOther')}</option>
      </select>
      {otherSelected && (
        <input
          type="text"
          className={styles.input}
          maxLength={RAW_UNIT_MAX}
          placeholder={t('portal.requestForm.lineUnitPlaceholder')}
          aria-label={t('portal.requestForm.unitOther')}
          autoFocus
          {...register(`lines.${index}.rawUnit`, { validate: (v) => validateRawUnit(v) })}
        />
      )}
    </>
  );
}

export default function RequestLinesForm({
  defaultValues,
  onDraftChange,
  onSubmit,
  isSubmitting,
  submitErrorId,
  submitError,
}) {
  const { t, lang } = useLanguage();
  const { register, control, handleSubmit, formState, setValue } = useForm({
    defaultValues: {
      neededBy: defaultValues?.neededBy || '',
      purpose: defaultValues?.purpose || '',
      buyerNote: defaultValues?.buyerNote || '',
      lines: defaultValues?.lines?.length ? defaultValues.lines : [],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
  const watched = useWatch({ control });

  // Entry Point 1 (catalog-picker task): the in-form "Add from catalog"
  // search. Collapsed by default — opening it is the ONLY thing that
  // enables usePortalProducts' query (see that hook's own comment), so
  // merely rendering this form never fetches the catalog. No debounce
  // utility added for this: search only fires on explicit submit (the
  // panel's own mini-form), not per keystroke, which is both simpler and
  // kinder to a buyer on slow mobile data than firing a request per
  // character typed.
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogInput, setCatalogInput] = useState('');
  const [catalogTerm, setCatalogTerm] = useState('');
  const catalogResultsQuery = usePortalProducts(catalogTerm, {
    enabled: catalogOpen && catalogTerm.trim().length > 0,
  });

  const toggleCatalogSearch = () => {
    setCatalogOpen((open) => !open);
    setCatalogInput('');
    setCatalogTerm('');
  };

  const submitCatalogSearch = (e) => {
    e.preventDefault();
    setCatalogTerm(catalogInput.trim());
  };

  // Appends the line (same shape "Request price"/the pending list already
  // build via utils/productDisplay.js#buildLineFromProduct), then
  // setValue's the three catalog-link fields explicitly — append() alone
  // seeds a NEW field array entry's default object (which is enough for
  // registered paths like rawDescription/rawUnit/qty), but
  // matchedProductId/productName/productSpec have no registered input
  // anywhere, so they need the same explicit setValue
  // admin/components/PricingLineRow.jsx already relies on for its own
  // analogous unregistered productId field, not just a implicit pass-
  // through from the appended object (see CatalogLineInfo's own comment).
  const handlePickFromCatalog = (product) => {
    const newIndex = fields.length;
    const line = buildLineFromProduct(product, lang);
    append(line);
    setValue(`lines.${newIndex}.matchedProductId`, line.matchedProductId);
    setValue(`lines.${newIndex}.productName`, line.productName);
    setValue(`lines.${newIndex}.productSpec`, line.productSpec);
    setCatalogOpen(false);
    setCatalogInput('');
    setCatalogTerm('');
  };

  // The attached file lives in plain component state, not react-hook-form —
  // a raw File isn't a field react-hook-form's own validation/reset model
  // fits, and it can't round-trip through this app's own localStorage draft
  // persistence either (Files aren't JSON-serializable) — reloading mid-
  // session keeps neededBy/purpose/buyerNote/lines as it always has, but drops a
  // not-yet-submitted file, same unavoidable limitation any browser form
  // has for an unsaved file input.
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const uploadInputRef = useRef(null);

  // The landing page's "Upload BOQ or PDF" button stashes a one-shot flag
  // (utils/pendingUploadFocus.js) picked up here, on mount, regardless of
  // whether this is the guest flow (which may take several steps to reach
  // this component) or the account-holder path (reaches it immediately).
  useEffect(() => {
    if (consumePendingUploadFocus()) {
      uploadInputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    onDraftChange?.(watched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watched)]);

  const noLines = fields.length === 0;
  const canSubmit = !noLines || Boolean(file);

  const handleFileSelect = (selected) => {
    setFileError(null);
    setFile(selected);
  };

  const handleFileRemove = () => {
    setFile(null);
    setFileError(null);
  };

  const submit = handleSubmit((values) => {
    onSubmit({
      neededBy: values.neededBy || undefined,
      purpose: values.purpose || undefined,
      buyerNote: values.buyerNote || undefined,
      lines: values.lines.map((line) => ({
        rawDescription: line.rawDescription.trim(),
        rawUnit: line.rawUnit?.trim() || undefined,
        qty: line.qty,
        matchedProductId: line.matchedProductId || undefined,
      })),
      file,
    });
  });

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <h1 className={styles.title}>{t('portal.requestForm.title')}</h1>

      <UploadDropZone
        ref={uploadInputRef}
        file={file}
        onFileSelect={handleFileSelect}
        onFileRemove={handleFileRemove}
        error={fileError}
      />

      <div className={styles.detailsGrid}>
        <label className={styles.field}>
          <span className={styles.label}>{t('portal.requestForm.neededBy')}</span>
          <input type="date" className={styles.input} {...register('neededBy')} />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t('portal.requestForm.purpose')}</span>
          <input
            type="text"
            className={styles.input}
            maxLength={255}
            placeholder={t('portal.requestForm.purposePlaceholder')}
            {...register('purpose')}
          />
        </label>

        <label className={`${styles.field} ${styles.fullRow}`}>
          <span className={styles.label}>{t('portal.requestForm.buyerNote')}</span>
          <textarea
            className={styles.textarea}
            maxLength={2000}
            placeholder={t('portal.requestForm.buyerNotePlaceholder')}
            {...register('buyerNote')}
          />
        </label>
      </div>

      <div className={styles.linesSection}>
        <div className={styles.linesHeader}>
          <h2 className={styles.linesTitle}>{t('portal.requestForm.linesTitle')}</h2>
          {!noLines && (
            <span className={`${styles.linesCount} tabularNums`}>
              {t('portal.requestForm.linesCount', { count: fields.length })}
            </span>
          )}
        </div>

        {noLines ? (
          <div className={styles.emptyLines}>
            <p className={styles.emptyLinesTitle}>{t('portal.requestForm.emptyLines.title')}</p>
            <p className={styles.emptyLinesBody}>
              {t(
                file
                  ? 'portal.requestForm.emptyLines.bodyWithFile'
                  : 'portal.requestForm.emptyLines.body'
              )}
            </p>
          </div>
        ) : (
          <>
            {/* Column headers — desktop only (RequestLinesForm.module.css
                hides this at mobile widths, where each line renders as a
                stacked card instead and repeats these same labels per
                field). Reuses the exact same string ids each input's own
                label already uses, just once instead of once per row. */}
            <div className={styles.lineListHeader} aria-hidden="true">
              <span>{t('portal.requestForm.lineDescription')}</span>
              <span>{t('portal.requestForm.lineUnit')}</span>
              <span>{t('portal.requestForm.lineQty')}</span>
              <span />
            </div>
            <ul className={styles.lineList}>
              {fields.map((field, index) => {
                const lineErrors = formState.errors?.lines?.[index] || {};
                return (
                  <li key={field.id} className={styles.lineRow}>
                    <hr className="hairline" />
                    <div className={styles.lineTopRow}>
                      <span className={`${styles.lineNo} tabularNums`}>{index + 1}</span>
                      <button
                        type="button"
                        className={styles.removeButton}
                        aria-label={t('portal.requestForm.removeLine')}
                        onClick={() => remove(index)}
                      >
                        <X size={18} aria-hidden="true" />
                      </button>
                    </div>

                    <CatalogLineInfo control={control} index={index} />

                    <label className={styles.field}>
                      <span className={styles.label}>
                        {t('portal.requestForm.lineDescription')}
                      </span>
                      <input
                        type="text"
                        className={styles.input}
                        maxLength={RAW_DESCRIPTION_MAX}
                        placeholder={t('portal.requestForm.lineDescriptionPlaceholder')}
                        {...register(`lines.${index}.rawDescription`, {
                          validate: (v) => validateRawDescription(v),
                        })}
                      />
                      {lineErrors.rawDescription && (
                        <span className={styles.fieldError}>
                          {t('portal.requestForm.descriptionRequired')}
                        </span>
                      )}
                    </label>

                    <div className={styles.lineGrid}>
                      <div className={styles.field}>
                        <span className={styles.label}>{t('portal.requestForm.lineUnit')}</span>
                        <UnitField
                          control={control}
                          register={register}
                          setValue={setValue}
                          index={index}
                        />
                      </div>

                      <label className={styles.field}>
                        <span className={styles.label}>{t('portal.requestForm.lineQty')}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${styles.input} tabularNums`}
                          {...register(`lines.${index}.qty`, { validate: (v) => validateQty(v) })}
                        />
                        {lineErrors.qty && (
                          <span className={styles.fieldError}>
                            {t('portal.requestForm.qtyInvalid')}
                          </span>
                        )}
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className={styles.addButtonsRow}>
          <button
            type="button"
            className={styles.addButton}
            onClick={() => append({ ...EMPTY_LINE })}
          >
            <Plus size={18} aria-hidden="true" />
            {t('portal.requestForm.addLine')}
          </button>
          <button
            type="button"
            className={styles.addButton}
            onClick={toggleCatalogSearch}
            aria-expanded={catalogOpen}
          >
            <Search size={18} aria-hidden="true" />
            {t('portal.requestForm.addFromCatalog')}
          </button>
        </div>

        {/* Entry Point 1 (catalog-picker task): inline, in place — never a
            navigation, so the buyer never risks losing what they've already
            typed above. */}
        {catalogOpen && (
          <div className={styles.catalogPanel}>
            <div className={styles.catalogSearchRow}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('portal.requestForm.catalogSearchPlaceholder')}
                value={catalogInput}
                onChange={(e) => setCatalogInput(e.target.value)}
                onKeyDown={(e) => {
                  // preventDefault: this input lives inside the OUTER
                  // <form>, and Enter would otherwise submit the whole
                  // request — it must only trigger the catalog search.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setCatalogTerm(catalogInput.trim());
                  }
                }}
                autoFocus
              />
              <button
                type="button"
                className={styles.catalogSearchButton}
                onClick={submitCatalogSearch}
              >
                {t('portal.requestForm.catalogSearchButton')}
              </button>
            </div>

            {catalogResultsQuery.isLoading && (
              <p className={styles.catalogHint}>{t('portal.requestForm.catalogSearching')}</p>
            )}
            {catalogResultsQuery.isError && !catalogResultsQuery.isLoading && (
              <ErrorState
                messageId="portal.requestForm.catalogSearchError"
                error={catalogResultsQuery.error}
              />
            )}
            {!catalogResultsQuery.isLoading &&
              !catalogResultsQuery.isError &&
              catalogTerm &&
              (catalogResultsQuery.data || []).length === 0 && (
                <p className={styles.catalogHint}>{t('portal.requestForm.catalogNoResults')}</p>
              )}

            {(catalogResultsQuery.data || []).length > 0 && (
              <ul className={styles.catalogResults}>
                {catalogResultsQuery.data.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className={styles.catalogResultRow}
                      onClick={() => handlePickFromCatalog(product)}
                    >
                      <span className={styles.catalogResultMain}>
                        <span className={styles.catalogResultName}>
                          {lang === 'am' && product.name_am ? product.name_am : product.name}
                        </span>
                        {product.display_description && (
                          <span className={styles.catalogResultSpec}>
                            {product.display_description}
                          </span>
                        )}
                      </span>
                      <span className={styles.catalogResultCondition}>
                        {t(conditionLabelId(product.condition))}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {submitErrorId && <ErrorState messageId={submitErrorId} error={submitError} />}

      <div className={styles.footer}>
        {/* !canSubmit already prevents handleSubmit from firing, so this
            hint explains WHY the button is disabled without an error alarm. */}
        {!canSubmit && <p className={styles.hint}>{t('portal.requestForm.lineRequired')}</p>}
        <button type="submit" className={styles.submitButton} disabled={isSubmitting || !canSubmit}>
          {isSubmitting && <Spinner />}{' '}
          {isSubmitting ? t('portal.requestForm.submitting') : t('portal.requestForm.submit')}
        </button>
      </div>
    </form>
  );
}
